
import { Eye, MousePointerClick, Smile, Tags } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { useAnalyticsPowers } from '@/hooks/use-analytics-powers';
import { AnalyticsPageChrome, AnalyticsPanel, AnalyticsPeriodBadge } from '@/components/painel/analytics/AnalyticsPageChrome';
import { usePainelText } from '@/components/painel/painel-texts-context';
import Link from '@/components/compat/Link';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(n);
}

export function PainelAnalyticsPowersPage() {
  const pageText = usePainelText('analytics_powers');
  const { data, loading } = useAnalyticsPowers();
  const top = data.topPowers;

  return (
    <AnalyticsPageChrome
      title={pageText.title}
      description={pageText.description}
      actions={<AnalyticsPeriodBadge>Últimos 30 dias</AnalyticsPeriodBadge>}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PainelStatCard
          title="Powers visitados"
          value={loading ? '—' : formatInt(data.totalViews)}
          icon={Eye}
          variant="sky"
        />
        <PainelStatCard
          title="Abrir Power"
          value={loading ? '—' : formatInt(data.openPowerClicks)}
          icon={MousePointerClick}
          description="Cliques no CTA"
          variant="cyan"
        />
        <PainelStatCard
          title="Ver Smilies"
          value={loading ? '—' : formatInt(data.viewSmiliesClicks)}
          icon={Smile}
          variant="indigo"
        />
        <PainelStatCard
          title="Categoria top"
          value={data.topCategory ?? '—'}
          icon={Tags}
          variant="violet"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticsPanel title="Powers mais visitados" subtitle="Ordenados por visualizações">
          <ul className="space-y-3">
            {top.slice(0, 6).map((row, i) => (
              <li
                key={row.slug}
                className="flex items-start gap-3 rounded-xl border border-white/[0.07] bg-white/[0.03] p-4"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/15 font-poppins text-xs font-bold text-sky-200">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-poppins text-sm font-semibold text-white">{row.name}</p>
                  <p className="mt-0.5 font-inter text-xs text-slate-400/80">{row.category ?? '—'}</p>
                  <div className="mt-2 flex flex-wrap gap-3 font-inter text-[11px] text-slate-400">
                    <span>{formatInt(row.views)} views</span>
                    <span>{formatInt(row.openPowerClicks)} abrir</span>
                    <span>{formatInt(row.viewSmiliesClicks)} smilies</span>
                  </div>
                </div>
                <Link
                  href={`/painel/powers/${row.slug}/edit`}
                  className="shrink-0 self-center font-poppins text-[11px] font-semibold text-sky-300/90 hover:text-sky-200"
                >
                  Editar
                </Link>
              </li>
            ))}
            {!loading && top.length === 0 ? (
              <p className="font-inter text-xs text-slate-400/80">Sem eventos de power no período.</p>
            ) : null}
          </ul>
        </AnalyticsPanel>

        <AnalyticsPanel title="Lançamentos recentes" subtitle="Powers recém-lançados mais acessados">
          <ul className="space-y-3">
            {data.recentLaunchAccess.map((row) => (
              <li
                key={row.slug}
                className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.07] bg-white/[0.03] px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-poppins text-sm font-semibold text-white">{row.name}</p>
                  <p className="font-inter text-xs text-slate-400">{row.category ?? '—'}</p>
                </div>
                <span className="shrink-0 font-poppins text-sm tabular-nums text-sky-200">
                  {formatInt(row.views)}
                </span>
              </li>
            ))}
            {!loading && data.recentLaunchAccess.length === 0 ? (
              <p className="font-inter text-xs text-slate-400/80">Sem dados de lançamentos ainda.</p>
            ) : null}
          </ul>
        </AnalyticsPanel>
      </div>

      <AnalyticsPanel title="Todos os powers com eventos" subtitle="Cliques e visualizações">
        <div className={cn(painelTableShellClass, 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-separate border-spacing-0">
              <thead>
                <tr className="bg-white/[0.03] font-poppins text-[10px] font-semibold uppercase tracking-wider text-sky-200/50">
                  <th className="px-4 py-3 text-left">Power</th>
                  <th className="px-4 py-3 text-left">Categoria</th>
                  <th className="px-4 py-3 text-right">Views</th>
                  <th className="px-4 py-3 text-right">Abrir</th>
                  <th className="px-4 py-3 text-right">Smilies</th>
                </tr>
              </thead>
              <tbody className="font-inter text-sm">
                {data.powers.map((row) => (
                  <tr key={row.slug} className="text-blue-100/90">
                    <td className="border-t border-white/[0.06] px-4 py-3">{row.name}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-slate-400">{row.category ?? '—'}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums">{formatInt(row.views)}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums">{formatInt(row.openPowerClicks)}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums">{formatInt(row.viewSmiliesClicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AnalyticsPanel>
    </AnalyticsPageChrome>
  );
}
